// Fix H (2026-05-28): heuristic for "this customer looks stuck."
//
// Powers the conditional surfacing of the WhatsApp help button in the
// editor's zoom toolbar. The button is invisible by default — it only
// shows up when the signals below trip, so it doesn't add noise for
// users who are flowing through the editor.
//
// Signals (any one trips):
//   - same slot touched > 3 times in the last 30s
//   - undo pressed > 2 times in the last 60s
//   - > 30s elapsed with no successful drop AND no successful save
//
// AND the user has been in the editor for at least 90s — early
// fumbling at the very start is expected, we don't want to greet
// fresh customers with a "need help?" pill.

import { useEffect, useRef, useState } from 'react';

const MIN_AGE_MS = 90_000;
const SLOT_REPEAT_WINDOW_MS = 30_000;
const SLOT_REPEAT_THRESHOLD = 3;
const UNDO_WINDOW_MS = 60_000;
const UNDO_THRESHOLD = 2;
const IDLE_PROGRESS_MS = 30_000;
const POLL_MS = 5_000;

export type StuckSignal = {
  type: 'slot_touch' | 'undo' | 'progress';
  /** Slot identifier — only used for `slot_touch`. */
  key?: string;
  /** Epoch milliseconds. */
  at: number;
};

export type StuckUserHandle = {
  isStuck: boolean;
  /** Record a slot interaction. */
  noteSlotTouch: (slotKey: string) => void;
  /** Record an undo press. */
  noteUndo: () => void;
  /** Record a meaningful progress event (successful drop, save, etc.).
   *  Resets the idle-progress timer. */
  noteProgress: () => void;
};

export function useStuckUser(): StuckUserHandle {
  const mountedAtRef = useRef<number>(Date.now());
  const slotTouchesRef = useRef<StuckSignal[]>([]);
  const undosRef = useRef<StuckSignal[]>([]);
  const lastProgressRef = useRef<number>(Date.now());

  const [isStuck, setIsStuck] = useState(false);

  const compute = () => {
    const now = Date.now();
    if (now - mountedAtRef.current < MIN_AGE_MS) {
      setIsStuck(false);
      return;
    }

    // Prune old samples
    slotTouchesRef.current = slotTouchesRef.current.filter(
      (s) => now - s.at < SLOT_REPEAT_WINDOW_MS,
    );
    undosRef.current = undosRef.current.filter((s) => now - s.at < UNDO_WINDOW_MS);

    // Signal 1: same slot touched > THRESHOLD times in the window.
    const slotCounts = new Map<string, number>();
    for (const s of slotTouchesRef.current) {
      if (!s.key) continue;
      slotCounts.set(s.key, (slotCounts.get(s.key) ?? 0) + 1);
    }
    const slotSpammed = [...slotCounts.values()].some((c) => c > SLOT_REPEAT_THRESHOLD);

    // Signal 2: undo > THRESHOLD in the window.
    const undoSpammed = undosRef.current.length > UNDO_THRESHOLD;

    // Signal 3: stuck without progress for > IDLE_PROGRESS_MS.
    const idleTooLong = now - lastProgressRef.current > IDLE_PROGRESS_MS;

    setIsStuck(slotSpammed || undoSpammed || idleTooLong);
  };

  useEffect(() => {
    // Poll periodically so the badge updates even when the user is idle
    // (no events firing to trigger re-render).
    const id = window.setInterval(compute, POLL_MS);
    return () => window.clearInterval(id);
  }, []);

  return {
    isStuck,
    noteSlotTouch: (slotKey: string) => {
      slotTouchesRef.current.push({ type: 'slot_touch', key: slotKey, at: Date.now() });
      compute();
    },
    noteUndo: () => {
      undosRef.current.push({ type: 'undo', at: Date.now() });
      compute();
    },
    noteProgress: () => {
      lastProgressRef.current = Date.now();
      compute();
    },
  };
}
